import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Close } from '@mui/icons-material';
import { updatePurchaseInfo, useWatchlistLabels } from '../services/api';

/**
 * PurchaseInfoModal - Modal for entering purchase date/price/shares
 *
 * @param {Object} props
 * @param {string} props.symbol - Stock symbol
 * @param {Object} props.label - Label object
 * @param {string} props.userEmail - User's email
 * @param {function} props.onClose - Called when modal is closed
 * @param {number} props.currentPrice - Current stock price (for reference)
 */
const PurchaseInfoModal = ({ symbol, label, userEmail, onClose, currentPrice }) => {
  const queryClient = useQueryClient();
  const { data: labelsData = { labels: [], assignments: {} } } = useWatchlistLabels(userEmail);

  // Get existing purchase data if any
  const existingPurchase = labelsData.assignments?.[symbol]?.purchases?.[label?.id];

  const [formData, setFormData] = useState({
    date: existingPurchase?.date || new Date().toISOString().split('T')[0],
    price: existingPurchase?.price || currentPrice || '',
    shares: existingPurchase?.shares || ''
  });
  const [error, setError] = useState('');

  // Update form when existing data or currentPrice loads
  useEffect(() => {
    setFormData(prev => ({
      date: existingPurchase?.date || prev.date || new Date().toISOString().split('T')[0],
      price: existingPurchase?.price || prev.price || currentPrice || '',
      shares: existingPurchase?.shares || prev.shares || ''
    }));
  }, [existingPurchase, currentPrice]);

  const mutation = useMutation({
    mutationFn: updatePurchaseInfo,
    onSuccess: () => {
      queryClient.invalidateQueries(['watchlistLabels', userEmail]);
      onClose();
    },
    onError: (err) => setError(err.message)
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    const purchase = {
      date: formData.date || null,
      price: formData.price ? parseFloat(formData.price) : null,
      shares: formData.shares ? parseFloat(formData.shares) : null
    };

    mutation.mutate({
      symbol,
      labelId: label.id,
      purchase
    });
  };

  const handleClear = () => {
    mutation.mutate({
      symbol,
      labelId: label.id,
      purchase: { date: null, price: null, shares: null }
    });
  };

  // Calculate gain/loss if we have purchase price and current price
  const calculateGainLoss = () => {
    if (!formData.price || !currentPrice) return null;
    const purchasePrice = parseFloat(formData.price);
    const gainLoss = ((currentPrice - purchasePrice) / purchasePrice) * 100;
    return gainLoss;
  };

  const calculateTotalValue = () => {
    if (!formData.shares || !currentPrice) return null;
    return parseFloat(formData.shares) * currentPrice;
  };

  const calculateTotalGainLoss = () => {
    if (!formData.shares || !formData.price || !currentPrice) return null;
    const purchaseValue = parseFloat(formData.shares) * parseFloat(formData.price);
    const currentValue = parseFloat(formData.shares) * currentPrice;
    return currentValue - purchaseValue;
  };

  const gainLoss = calculateGainLoss();
  const totalValue = calculateTotalValue();
  const totalGainLoss = calculateTotalGainLoss();

  if (!label) return null;

  return (
    <div className="w-80">
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{symbol}</h3>
          <div className="flex items-center gap-2 mt-1">
            <span
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: label.color }}
            />
            <span className="text-sm text-gray-500">{label.name}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-600 rounded"
        >
          <Close style={{ fontSize: 20 }} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-4 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Purchase Date
          </label>
          <input
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Purchase Price ($)
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={formData.price}
            onChange={(e) => setFormData({ ...formData, price: e.target.value })}
            placeholder="0.00"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Number of Shares
          </label>
          <input
            type="number"
            step="0.0001"
            min="0"
            value={formData.shares}
            onChange={(e) => setFormData({ ...formData, shares: e.target.value })}
            placeholder="0"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Summary section */}
        {(gainLoss !== null || totalValue !== null) && (
          <div className="bg-gray-50 rounded-md p-3 space-y-2">
            {currentPrice && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Current Price:</span>
                <span className="font-medium">${currentPrice.toFixed(2)}</span>
              </div>
            )}
            {gainLoss !== null && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Gain/Loss:</span>
                <span className={`font-medium ${gainLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {gainLoss >= 0 ? '+' : ''}{gainLoss.toFixed(2)}%
                </span>
              </div>
            )}
            {totalValue !== null && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Total Value:</span>
                <span className="font-medium">${totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
            )}
            {totalGainLoss !== null && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Total Gain/Loss:</span>
                <span className={`font-medium ${totalGainLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {totalGainLoss >= 0 ? '+' : ''}${totalGainLoss.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="text-sm text-red-600 bg-red-50 p-2 rounded-md border border-red-200">
            {error}
          </div>
        )}

        <div className="flex justify-between pt-2">
          {existingPurchase && (existingPurchase.date || existingPurchase.price || existingPurchase.shares) ? (
            <button
              type="button"
              onClick={handleClear}
              disabled={mutation.isLoading}
              className="px-3 py-1.5 text-sm text-red-600 hover:text-red-800"
            >
              Clear Data
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isLoading}
              className="px-4 py-1.5 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
            >
              {mutation.isLoading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PurchaseInfoModal;
